import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Badge
} from '@mui/material';
import { Person, ArrowBack, Logout, Notifications, BugReport, DarkMode, LightMode } from '@mui/icons-material';
import logo from '../../static/logo_main_3124x512.png';
import logoDark from '../../static/logo_main_dark_3124x512.png';
import { useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import BugReportModal from '../modals/BugReportModal/BugReportModal';
import { putBug } from '../../lib/connector/miscQueries';
import { v4 as uuidv4 } from 'uuid';

interface MainHeaderProps {
  title: string;
  subTitle: string;
  onClickLogo: () => void;
  onClickProfile?: () => void;
  onClickLogout?: () => void;
  onBack?: () => void;
  onClickNotifications?: () => void;
  userEmail?: string; // Add userEmail prop
}

const MainHeader: React.FC<MainHeaderProps> = ({ 
  title, 
  subTitle, 
  onClickLogo, 
  onClickProfile, 
  onClickLogout, 
  onBack, 
  onClickNotifications,
  userEmail
}) => {
  const { isDarkMode, toggleTheme } = useThemeContext();
  const [showBugReportModal, setShowBugReportModal] = useState(false);

  const handleBugReportSubmit = async (bugReport: any) => {
    putBug(uuidv4(), bugReport.title, bugReport.summary)
  };

  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          {onBack ? (
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, overflow: { xs: 'scroll', sm: 'hidden' } }}>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={900}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '180px', sm: '100%' }
                }}
              >
                {title}
              </Typography>
              <Typography variant="caption" fontWeight={600} color="text.secondary">{subTitle}</Typography>
            </Box>
            <Box sx={{
              width: { xs: '0%', sm: '60%', md: '70%' },
              ml: 1,
              borderLeft: 1,
              borderColor: 'text.secondary',
              pl: 1,
              display: 'flex',
              alignItems: 'center'
            }}>
              <img
                src={isDarkMode ? logoDark : logo}
                onClick={onClickLogo}
                alt="Stafflows Logo"
                style={{
                  width: "clamp(120px, 20%, 240px)",
                  maxWidth: "100%",
                  height: "auto",
                  cursor: 'pointer'
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1 } }}>
            {(
              <IconButton color={isDarkMode ? 'info' : 'primary'} onClick={toggleTheme}>
                {isDarkMode ? <LightMode /> : <DarkMode />}
              </IconButton>
            )}
            {onClickNotifications && (
              <IconButton color='info' onClick={onClickNotifications}>
                <Badge badgeContent=" " variant="dot" color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            )}
            {onClickProfile && (
              <IconButton color={isDarkMode ? 'primary' : 'info'} onClick={onClickProfile}>
                <Person />
              </IconButton>
            )}
            {(
              <IconButton 
                color={isDarkMode ? 'primary' : 'info'} 
                onClick={() => setShowBugReportModal(true)}
                title="Report a Bug"
              >
                <BugReport />
              </IconButton>
            )}
            {onClickLogout && (
              <IconButton color="error" onClick={onClickLogout}>
                <Logout />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Bug Report Modal */}
      <BugReportModal
        open={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
        onSubmit={handleBugReportSubmit}
        userEmail={userEmail}
      />
    </>
  );
};

export default MainHeader;